import {css} from 'aphrodite';
import React from 'react';

import {ArchiveOptions} from 'archive/ArchiveConstants';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ResourcePresenceManager from 'nav/presence/ResourcePresenceManager';
import ResourcePresenceView from 'nav/presence/ResourcePresenceView';
import {NO_TEMPLATE_COLLECTION} from 'notebook/canvas/GridTitle';
import GridTitleNameEditor from 'notebook/canvas/GridTitleNameEditor';
import gridTitleStyles from 'notebook/canvas/gridTitleStyles';
import {DocumentHistoryButton} from 'notebook/documents/DocumentHistoryButton';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import notifications from 'notifications';
import EditRequestV2TemplateModal, {
  ProcessTaskSchemaDetails,
} from 'processes/requests_v2/modals/EditRequestV2TemplateModal';
import {
  RequestV2DefinitionDetails,
  updateAndCacheRequestV2Definition,
} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionPageQueryUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Badge, {BadgeColor} from 'ui-library/Badge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

import {UpdateRequestV2DefinitionParams} from '../network/RequestV2DefinitionDAO';

export interface RequestV2DefinitionHeaderProps {
  isReadOnly: boolean;
  requestV2Definition: RequestV2DefinitionDetails;
}

interface EditRequestV2DefinitionButtonProps {
  requestV2DefinitionId: string;
  prefix: string;
  description: string | null;
  processTaskSchema: ProcessTaskSchemaDetails | null;
}

export function EditRequestV2DefinitionButton(props: EditRequestV2DefinitionButtonProps): JSX.Element {
  return (
    <div className={css(gridTitleStyles.secondaryButton)}>
      <Tooltip text="View & edit template information" placement={FloatingPlacement.BOTTOM}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          icon="info-circle"
          size={ButtonSize.MEDIUM}
          dataTestAttrs={{component: 'RequestV2DefinitionHeader', element: 'editButton'}}
          onClick={() => {
            legacyRunModal(EditRequestV2TemplateModal, {
              requestV2DefinitionId: props.requestV2DefinitionId,
              prefix: props.prefix,
              description: props.description,
              processTaskSchema: props.processTaskSchema ?? null,
            });
          }}
        />
      </Tooltip>
    </div>
  );
}

export default function RequestV2DefinitionHeader(props: RequestV2DefinitionHeaderProps): JSX.Element {
  const isArchived = props.requestV2Definition?.archiveData?.archived ?? false;

  const updateRequestV2Definition = async (
    params: UpdateRequestV2DefinitionParams,
    loadingMessage: string
  ): Promise<void> => {
    try {
      await updateAndCacheRequestV2Definition(params, loadingMessage);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON?.errors?.name != null) {
        notifications.error(e.responseJSON.errors.name);
      } else {
        notifications.notifyError(e);
      }
    }
  };

  const overflowMenuOptions: Array<OverflowMenuOption> = [
    {
      icon: 'move',
      contents: <span>Move</span>,
      isDisabled: props.isReadOnly,
      onSelect: () => {
        legacyRunModal(MoveToTemplateCollectionModal, {
          initialTemplateCollectionId: props.requestV2Definition.templateCollection.id,
          onRequestSubmit: (templateCollectionId) => {
            updateRequestV2Definition(
              {
                id: props.requestV2Definition.id,
                templateCollectionId,
                actionOrigin: ActionOrigin.REQUEST_V2_DEFINITION_CONFIG_PAGE,
                actionMethod: ActionMethod.DIRECT_EDIT,
              },
              'Updating template collection...'
            );
          },
          title: 'Move request template',
        });
      },
    },
    {
      icon: isArchived ? 'unarchive' : 'archive',
      contents: <span>{isArchived ? 'Unarchive' : 'Archive'} request template</span>,
      isDisabled: props.isReadOnly && !isArchived,
      onSelect: async () => {
        if (isArchived) {
          await updateAndCacheRequestV2Definition(
            {
              id: props.requestV2Definition.id,
              isArchived: false,
              actionOrigin: ActionOrigin.REQUEST_V2_DEFINITION_CONFIG_PAGE,
              actionMethod: ActionMethod.BUTTON_CLICK,
            },
            'Unarchiving request template...'
          );
        } else {
          ArchiveModal.open({
            items: [{id: props.requestV2Definition.id, name: props.requestV2Definition.name}],
            onArchive: async (itemIds: Array<string>, purpose: string, opts: ArchiveOptions) => {
              assert(
                itemIds.length === 1 && itemIds[0] === props.requestV2Definition.id,
                'Expected exactly one request template to be archived',
                {itemIds, requestV2DefinitionId: props.requestV2Definition.id}
              );
              await updateAndCacheRequestV2Definition(
                {
                  id: props.requestV2Definition.id,
                  isArchived: true,
                  archiveReason: purpose,
                  actionOrigin: ActionOrigin.REQUEST_V2_DEFINITION_CONFIG_PAGE,
                  actionMethod: ActionMethod.BUTTON_CLICK,
                },
                'Archiving request template...'
              );
            },
          });
        }
      },
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        AuditActions.exportAuditLogs([props.requestV2Definition.id]);
      },
    },
  ];

  const getEditButton = (): JSX.Element | null => {
    if (isInaccessible(props.requestV2Definition.workflowTaskSchema)) {
      return null;
    } else {
      return (
        <EditRequestV2DefinitionButton
          requestV2DefinitionId={props.requestV2Definition.id}
          prefix={props.requestV2Definition.prefix}
          description={props.requestV2Definition.description}
          processTaskSchema={props.requestV2Definition.workflowTaskSchema}
        />
      );
    }
  };

  return (
    <div className={css(gridTitleStyles.gridTitleContainer)}>
      <div className={css(gridTitleStyles.gridTitleInfoContainer)}>
        <Tooltip text="Request Template" placement={FloatingPlacement.BOTTOM_START}>
          <div className={css(gridTitleStyles.gridTitleIcon)}>
            <BenchlingIcon icon="request-template" className={css(gridTitleStyles.gridTitleIcon)} />
          </div>
        </Tooltip>
        <div className={css(gridTitleStyles.gridTitleInfo)}>
          {props.requestV2Definition.templateCollection.url ? (
            <div
              className={css(
                gridTitleStyles.gridTitleTemplateCollectionName,
                gridTitleStyles.withTrailingSlash
              )}
            >
              <a
                href={props.requestV2Definition.templateCollection.url}
                {...getTestProps({component: 'RequestV2DefinitionHeader', element: 'templateCollectionName'})}
              >
                {props.requestV2Definition.templateCollection.name}
              </a>
            </div>
          ) : (
            <div
              className={css(
                gridTitleStyles.gridTitleTemplateCollectionNameNoLink,
                gridTitleStyles.withTrailingSlash
              )}
            >
              {NO_TEMPLATE_COLLECTION}
            </div>
          )}
          <div className="u-lr-split">
            <div className={css(gridTitleStyles.gridTitleTemplateName)}>
              <GridTitleNameEditor
                name={props.requestV2Definition.name}
                onRename={async (title: string) => {
                  await updateRequestV2Definition(
                    {
                      id: props.requestV2Definition.id,
                      name: title,
                      actionOrigin: ActionOrigin.REQUEST_V2_DEFINITION_CONFIG_PAGE,
                      actionMethod: ActionMethod.DIRECT_EDIT,
                    },
                    'Updating request template name...'
                  );
                }}
                titleDisabled={props.isReadOnly}
              />
            </div>
            {BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE && (
              <Badge
                startingIcon={ModelTypeConstants.TEMPLATE_COLLECTION}
                color={BadgeColor.COLLECTIBLE_BLUE}
              >
                Template
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className={css(gridTitleStyles.gridTitleOptions)}>
        <div className={css(gridTitleStyles.resourcePresenceContainer)}>
          <ResourcePresenceManager fileId={props.requestV2Definition.templateDocument.id}>
            <ResourcePresenceView resourceId={props.requestV2Definition.templateDocument.id} />
          </ResourcePresenceManager>
        </div>
        <DocumentHistoryButton
          documentId={props.requestV2Definition.templateDocument.id}
          title="REQUEST TEMPLATE HISTORY"
          tooltipText="Request Template History"
        />
        {getEditButton()}
        <OverflowMenu
          options={overflowMenuOptions}
          menuProps={{dropLeft: true}}
          buttonProps={{
            size: ButtonSize.MEDIUM,
            dataTestAttrs: {component: 'RequestDocEditorHeader', element: 'overflowMenu'},
          }}
        />
      </div>
    </div>
  );
}
